import React, { useEffect, useState, useMemo } from "react";
import Modal from "react-modal";
import { useTheme, getCssVar } from "../../hooks/useTheme";
import { i18n, LANG_KEY } from "./i18n";

const API = "https://api.fullagro.com:8888";

const buildStyles = (p) => ({
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.85)",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    zIndex: 2000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  content: {
    background: p.surface,
    color: p.text,
    borderRadius: 16,
    padding: 0,
    width: "min(1100px, 96vw)",
    height: "min(86vh, 860px)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "row",
    position: "relative",
    border: `1px solid ${p.border}`,
    boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
  },
  sidebar: {
    width: 220,
    flexShrink: 0,
    background: p.bgCard,
    borderRight: `1px solid ${p.border}`,
    padding: "20px 12px",
    overflowY: "auto",
  },
  main: {
    flex: 1,
    padding: "24px 32px",
    overflowY: "auto",
  },
  brand: {
    padding: "0 8px 18px 8px",
    borderBottom: `1px solid ${p.border}`,
    marginBottom: 14,
  },
  brandTitle: {
    fontWeight: 700,
    fontSize: 13,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    color: p.muted,
  },
  brandApp: {
    fontWeight: 700,
    fontSize: 17,
    marginTop: 4,
    color: p.text,
  },
  brandVer: {
    fontSize: 11,
    color: p.muted,
    marginTop: 2,
    fontFamily: "monospace",
  },
  langToggle: {
    display: "inline-flex",
    gap: 0,
    marginTop: 8,
    background: p.bg,
    border: `1px solid ${p.border}`,
    borderRadius: 999,
    padding: 2,
    fontSize: 11,
    fontWeight: 700,
    fontFamily: "monospace",
  },
  langBtn: (active) => ({
    background: active ? p.accent : "transparent",
    color: active ? "#fff" : p.muted,
    border: "none",
    padding: "3px 10px",
    borderRadius: 999,
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    fontFamily: "monospace",
    transition: "background 100ms",
  }),
  navItem: (active) => ({
    display: "block",
    width: "100%",
    textAlign: "left",
    background: active ? p.accent : "transparent",
    color: active ? "#fff" : p.text,
    border: "none",
    padding: "10px 12px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: active ? 700 : 500,
    marginBottom: 4,
    fontFamily: "inherit",
    transition: "background 100ms",
  }),
  h1: {
    fontSize: 22,
    fontWeight: 700,
    margin: "0 0 6px 0",
    color: p.text,
  },
  subtitle: {
    fontSize: 13,
    color: p.muted,
    marginBottom: 20,
    fontFamily: "monospace",
  },
  h2: {
    fontSize: 15,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: p.accent,
    margin: "20px 0 10px 0",
  },
  h3: {
    fontSize: 14,
    fontWeight: 700,
    color: p.text,
    margin: "12px 0 6px 0",
  },
  p: {
    fontSize: 13,
    lineHeight: 1.55,
    color: p.text,
    margin: "0 0 8px 0",
  },
  muted: {
    color: p.muted,
    fontSize: 12,
  },
  code: {
    background: p.bg,
    border: `1px solid ${p.border}`,
    color: p.text,
    padding: "1px 6px",
    borderRadius: 4,
    fontFamily: "monospace",
    fontSize: 12,
  },
  pre: {
    background: p.bg,
    border: `1px solid ${p.border}`,
    color: p.text,
    padding: 12,
    borderRadius: 8,
    fontFamily: "monospace",
    fontSize: 12,
    overflowX: "auto",
    margin: "6px 0 12px 0",
    lineHeight: 1.5,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
    marginTop: 6,
  },
  th: {
    textAlign: "left",
    padding: "8px 10px",
    background: p.bgCard,
    borderBottom: `1px solid ${p.border}`,
    color: p.muted,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    fontSize: 11,
  },
  td: {
    padding: "8px 10px",
    borderBottom: `1px solid ${p.border}`,
    color: p.text,
    verticalAlign: "top",
  },
  pill: (variant) => {
    const map = {
      admin: { bg: "#b3382e", fg: "#fff" },
      auth: { bg: "#1d6fc9", fg: "#fff" },
      catalog: { bg: "#2e7d32", fg: "#fff" },
      sync: { bg: "#b8921b", fg: "#000" },
      asset: { bg: "#7a3eb8", fg: "#fff" },
    };
    const c = map[variant] || { bg: p.border, fg: p.text };
    return {
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: 10,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: 0.6,
      textTransform: "uppercase",
      background: c.bg,
      color: c.fg,
    };
  },
  close: {
    position: "absolute",
    top: 12,
    right: 12,
    background: p.bgCard,
    border: `1px solid ${p.border}`,
    color: p.text,
    borderRadius: 8,
    width: 36,
    height: 36,
    cursor: "pointer",
    fontSize: 18,
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  loading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: p.muted,
    fontSize: 14,
  },
  footerNote: {
    marginTop: 24,
    padding: "12px 16px",
    background: p.bgCard,
    border: `1px solid ${p.border}`,
    borderRadius: 8,
    fontSize: 12,
    color: p.muted,
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 10,
    marginTop: 12,
  },
  statCard: {
    background: p.bgCard,
    border: `1px solid ${p.border}`,
    borderRadius: 10,
    padding: "12px 14px",
  },
  statLabel: {
    fontSize: 10,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: p.muted,
    fontWeight: 700,
    marginBottom: 6,
  },
  statValue: {
    fontSize: 22,
    fontWeight: 700,
    color: p.text,
    fontFamily: "monospace",
    lineHeight: 1.1,
  },
  statSub: {
    fontSize: 11,
    color: p.muted,
    marginTop: 4,
  },
  statValueAccent: {
    fontSize: 22,
    fontWeight: 700,
    color: p.accent,
    fontFamily: "monospace",
    lineHeight: 1.1,
  },
  intro: {
    background: p.bgCard,
    border: `1px solid ${p.border}`,
    borderRadius: 10,
    padding: "14px 16px",
    marginBottom: 14,
  },
  introRow: {
    marginBottom: 8,
  },
  introLabel: {
    fontSize: 10,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: p.accent,
    fontWeight: 700,
    marginBottom: 4,
  },
  callout: (variant) => {
    const map = {
      info: { bg: "rgba(45, 124, 250, 0.10)", border: "#1d6fc9", fg: "#1d6fc9" },
      warn: { bg: "rgba(184, 146, 27, 0.12)", border: "#b8921b", fg: "#8a6c0d" },
      danger: { bg: "rgba(179, 56, 46, 0.10)", border: "#b3382e", fg: "#b3382e" },
      ok: { bg: "rgba(46, 125, 50, 0.10)", border: "#2e7d32", fg: "#2e7d32" },
    };
    const c = map[variant] || map.info;
    return {
      background: c.bg,
      borderLeft: `4px solid ${c.border}`,
      padding: "12px 14px",
      borderRadius: 6,
      marginTop: 12,
      marginBottom: 12,
    };
  },
  calloutTitle: (variant) => {
    const map = {
      info: "#1d6fc9",
      warn: "#8a6c0d",
      danger: "#b3382e",
      ok: "#2e7d32",
    };
    return {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.8,
      textTransform: "uppercase",
      color: map[variant] || map.info,
      marginBottom: 6,
    };
  },
  calloutBody: {
    fontSize: 13,
    lineHeight: 1.55,
    color: p.text,
  },
});

const SECTIONS = [
  { id: "overview", key: "overview" },
  { id: "sovereignty", key: "sovereignty" },
  { id: "endpoints", key: "endpoints" },
  { id: "sync", key: "sync" },
  { id: "database", key: "database" },
  { id: "operations", key: "operations" },
  { id: "troubleshooting", key: "troubleshooting" },
];

export default function DocModal({ isOpen, onRequestClose }) {
  const theme = useTheme();
  const isLight = theme === "light";
  const styles = useMemo(
    () =>
      buildStyles({
        text: getCssVar("--fa-text", isLight ? "#0a0e0a" : "#e5ede5"),
        muted: getCssVar("--fa-muted", isLight ? "#5a6a5a" : "#9aa89a"),
        bg: getCssVar("--fa-bg", isLight ? "#f6f9f5" : "#0a0e0a"),
        surface: getCssVar("--fa-surface", isLight ? "#ffffff" : "#0e130e"),
        bgCard: getCssVar("--fa-bg-card", isLight ? "#ffffff" : "#121a12"),
        border: getCssVar("--fa-border", isLight ? "#cfd8cf" : "#2a3a2a"),
        accent: getCssVar("--fa-accent", "#2e7d32"),
      }),
    [isLight]
  );

  const [docs, setDocs] = useState(null);
  const [error, setError] = useState(null);
  const [section, setSection] = useState("overview");
  const [lang, setLang] = useState(() => {
    try {
      const saved = localStorage.getItem(LANG_KEY);
      return saved === "en" || saved === "es" ? saved : "es";
    } catch {
      return "es";
    }
  });

  useEffect(() => {
    try { localStorage.setItem(LANG_KEY, lang); } catch {}
  }, [lang]);

  const t = useMemo(() => i18n[lang], [lang]);

  useEffect(() => {
    if (!isOpen) return;
    setDocs(null);
    setError(null);
    setSection("overview");
    const token = localStorage.getItem("token");
    fetch(`${API}/api/docs`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(setDocs)
      .catch((e) => setError(e.message));
  }, [isOpen]);

  if (!isOpen) return null;

  const renderEndpoint = (e, t) => (
    <tr key={e.method + e.path}>
      <td style={styles.td}>
        <span style={{ ...styles.pill(e.admin_only ? "admin" : "auth") }}>{e.method}</span>
      </td>
      <td style={styles.td}>
        <code style={styles.code}>{e.path}</code>
      </td>
      <td style={styles.td}>{e.description}</td>
      <td style={styles.td}>
        {e.admin_only ? <span style={styles.pill("admin")}>{t.common.admin}</span> : <span style={styles.muted}>{t.common.allUsers}</span>}
      </td>
    </tr>
  );

  return (
    <div style={styles.overlay} onClick={onRequestClose}>
      <div style={styles.content} onClick={(e) => e.stopPropagation()}>
        <button style={styles.close} onClick={onRequestClose} aria-label={t.common.close}>×</button>

        <aside style={styles.sidebar}>
          <div style={styles.brand}>
            <div style={styles.brandTitle}>{lang === "es" ? "Docs del sistema" : "System Docs"}</div>
            {docs && (
              <>
                <div style={styles.brandApp}>{docs.app.name}</div>
                <div style={styles.brandVer}>v{docs.app.version} · {new Date(docs.generated_at).toLocaleString()}</div>
              </>
            )}
            <div style={styles.langToggle}>
              <button style={styles.langBtn(lang === "es")} onClick={() => setLang("es")}>ES</button>
              <button style={styles.langBtn(lang === "en")} onClick={() => setLang("en")}>EN</button>
            </div>
          </div>
          {SECTIONS.map((s) => (
            <button key={s.id} style={styles.navItem(section === s.id)} onClick={() => setSection(s.id)}>
              {t.nav[s.key]}
            </button>
          ))}
        </aside>

        <main style={styles.main}>
          {error && <p style={{ color: "#e87a7a" }}>Error: {error}</p>}
          {!docs && !error && <div style={styles.loading}>{t.common.loading}</div>}
          {docs && section === "overview" && (
            <>
              <h1 style={styles.h1}>{t.overview.title}</h1>
              <p style={styles.subtitle}>
                {docs.app.api_base} · {docs.app.app_base} · {docs.today}
              </p>

              <div style={styles.intro}>
                <div style={styles.introRow}>
                  <div style={styles.introLabel}>{t.overview.whatLabel}</div>
                  <div style={styles.p}>{docs.platform_summary.what}</div>
                </div>
                <div style={styles.introRow}>
                  <div style={styles.introLabel}>{t.overview.howLabel}</div>
                  <div style={styles.p}>{docs.platform_summary.how}</div>
                </div>
                <div style={styles.introRow}>
                  <div style={styles.introLabel}>{t.overview.forWhomLabel}</div>
                  <div style={styles.p}>{docs.platform_summary.for_whom}</div>
                </div>
              </div>

              <h2 style={styles.h2}>{t.overview.liveStats}</h2>
              <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>{t.overview.statPhotos}</div>
                  <div style={styles.statValueAccent}>{docs.live_stats.photos_on_disk.toLocaleString()}</div>
                  <div style={styles.statSub}>{docs.live_stats.photos_size_human} en disco</div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>{t.overview.statPdfs}</div>
                  <div style={styles.statValueAccent}>{docs.live_stats.pdfs_on_disk.toLocaleString()}</div>
                  <div style={styles.statSub}>{docs.live_stats.pdfs_size_human} en disco</div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>{t.overview.statProducts}</div>
                  <div style={styles.statValue}>{docs.live_stats.products_total.toLocaleString()}</div>
                  <div style={styles.statSub}>
                    {docs.live_stats.products_with_plant_url} con foto · {docs.live_stats.products_with_pdf_url} con PDF
                  </div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>{t.overview.statLocalPdf}</div>
                  <div style={styles.statValue}>
                    {docs.live_stats.products_with_local_pdf}/{docs.live_stats.products_with_pdf_url}
                  </div>
                  <div style={styles.statSub}>PDFs servidos desde local</div>
                </div>
              </div>

              {docs.live_stats.last_sync && (
                <div style={{ ...styles.intro, marginTop: 14 }}>
                  <div style={styles.introLabel}>{t.overview.statLastSync}</div>
                  <div style={styles.p}>
                    <code style={styles.code}>
                      {new Date(docs.live_stats.last_sync.started_at).toLocaleString()}
                    </code>
                    {" · "}
                    <span style={{ ...styles.pill(docs.live_stats.last_sync.status === "success" ? "catalog" : "admin") }}>
                      {docs.live_stats.last_sync.status}
                    </span>
                    {" · fetched="}{docs.live_stats.last_sync.fetched ?? 0}
                    {", inserted="}{docs.live_stats.last_sync.inserted ?? 0}
                    {", updated="}{docs.live_stats.last_sync.updated ?? 0}
                    {", failed="}{docs.live_stats.last_sync.failed ?? 0}
                  </div>
                </div>
              )}

              <h2 style={styles.h2}>{t.overview.architecture}</h2>
              <p style={styles.p}>{docs.architecture.summary}</p>
              <ul style={{ ...styles.p, paddingLeft: 18 }}>
                {docs.architecture.layers.map((l, i) => <li key={i}>{l}</li>)}
              </ul>

              <div style={styles.footerNote}>
                {t.overview.footer(<code style={styles.code}>{docs.app.api_base}</code>)}
              </div>
            </>
          )}

          {docs && section === "sovereignty" && (
            <>
              <h1 style={styles.h1}>{t.sovereignty.title}</h1>
              <p style={styles.p}>{docs.local_sovereignty.summary}</p>
              <h2 style={styles.h2}>{t.sovereignty.storage}</h2>
              <table style={styles.table}>
                <thead>
                  <tr><th style={styles.th}>{t.common.type}</th><th style={styles.th}>{t.common.path}</th></tr>
                </thead>
                <tbody>
                  <tr><td style={styles.td}>{t.sovereignty.photos}</td><td style={styles.td}><code style={styles.code}>{docs.local_sovereignty.storage_paths.photos}</code></td></tr>
                  <tr><td style={styles.td}>{t.sovereignty.pdfs}</td><td style={styles.td}><code style={styles.code}>{docs.local_sovereignty.storage_paths.pdfs}</code></td></tr>
                </tbody>
              </table>
              <h2 style={styles.h2}>{t.sovereignty.naming}</h2>
              <table style={styles.table}>
                <thead>
                  <tr><th style={styles.th}>{t.common.asset}</th><th style={styles.th}>{t.common.filenamePattern}</th></tr>
                </thead>
                <tbody>
                  <tr><td style={styles.td}>{t.sovereignty.plantPhoto}</td><td style={styles.td}><code style={styles.code}>{docs.local_sovereignty.naming_convention.plant_photo}</code></td></tr>
                  <tr><td style={styles.td}>{t.sovereignty.seedPhoto}</td><td style={styles.td}><code style={styles.code}>{docs.local_sovereignty.naming_convention.seed_photo}</code></td></tr>
                  <tr><td style={styles.td}>{t.sovereignty.germinationPdf}</td><td style={styles.td}><code style={styles.code}>{docs.local_sovereignty.naming_convention.germination_pdf}</code></td></tr>
                </tbody>
              </table>

              <h2 style={styles.h2}>{t.sovereignty.downloadFlow}</h2>
              <div style={styles.callout("info")}>
                <div style={styles.calloutTitle("info")}>{t.sovereignty.flowHow}</div>
                <div style={styles.calloutBody}>{docs.local_sovereignty.download_flow.how}</div>
              </div>
              <h3 style={styles.h3}>{t.sovereignty.flowWhatHappens}</h3>
              <ol style={{ ...styles.p, paddingLeft: 18 }}>
                {docs.local_sovereignty.download_flow.what_happens_per_run.map((s, i) => <li key={i}>{s}</li>)}
              </ol>

              <div style={styles.callout("ok")}>
                <div style={styles.calloutTitle("ok")}>{t.sovereignty.flowDownloadsAll}</div>
                <div style={styles.calloutBody}>{t.sovereignty.flowDownloadsAllNo}</div>
              </div>
              <div style={styles.callout("ok")}>
                <div style={styles.calloutTitle("ok")}>{t.sovereignty.flowUpdatesValues}</div>
                <div style={styles.calloutBody}>{t.sovereignty.flowUpdatesValuesYes}</div>
              </div>
              <div style={styles.callout("ok")}>
                <div style={styles.calloutTitle("ok")}>{t.sovereignty.flowRedownloads}</div>
                <div style={styles.calloutBody}>{t.sovereignty.flowRedownloadsNo}</div>
              </div>

              <table style={styles.table}>
                <tbody>
                  <tr>
                    <td style={{ ...styles.td, width: 180 }}>{t.sovereignty.flowInterval}</td>
                    <td style={styles.td}><code style={styles.code}>{docs.sync_engine.interval_minutes_default} min</code> ({t.sovereignty.flowManualTrigger}: {docs.local_sovereignty.download_flow.current_interval_minutes})</td>
                  </tr>
                  <tr>
                    <td style={styles.td}>{t.sovereignty.flowManualTrigger}</td>
                    <td style={styles.td}>{t.sovereignty.flowManualTriggerText}</td>
                  </tr>
                </tbody>
              </table>

              <h2 style={styles.h2}>{t.sovereignty.behavior}</h2>
              <ul style={{ ...styles.p, paddingLeft: 18 }}>
                {docs.local_sovereignty.behavior.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
              <h2 style={styles.h2}>{t.sovereignty.fallback}</h2>
              <p style={styles.p}>
                <strong>{t.sovereignty.photos}</strong>: {t.sovereignty.photosFallback(
                  <code style={styles.code}>/image-proxy?alpha=X&type=plant|seed</code>,
                  <code style={styles.code}>{docs.local_sovereignty.storage_paths.photos}</code>,
                  <code style={styles.code}>{`{Alpha_Batch_Id}_plant/seed.{jpg|png|webp|gif}`}</code>,
                  <code style={styles.code}>products.Plant_Image</code>
                )}
              </p>
              <p style={styles.p}>
                <strong>{t.sovereignty.pdfs}</strong>: {t.sovereignty.pdfsFallback(
                  <code style={styles.code}>/pdf-proxy?alpha=X</code>,
                  <code style={styles.code}>{docs.local_sovereignty.storage_paths.pdfs}</code>,
                  <code style={styles.code}>Germination_Test_Link_PDF</code>
                )}
              </p>
            </>
          )}

          {docs && section === "endpoints" && (
            <>
              <h1 style={styles.h1}>{t.endpoints.title}</h1>
              {Object.entries(docs.endpoints).map(([group, list]) => (
                <div key={group}>
                  <h2 style={styles.h2}>{t.endpoints.groups[group] || group}</h2>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>{t.common.method}</th>
                        <th style={styles.th}>{t.common.path}</th>
                        <th style={styles.th}>{t.common.description}</th>
                        <th style={styles.th}>{t.common.auth}</th>
                      </tr>
                    </thead>
                    <tbody>{list.map((e) => renderEndpoint(e, t))}</tbody>
                  </table>
                </div>
              ))}
            </>
          )}

          {docs && section === "sync" && (
            <>
              <h1 style={styles.h1}>{t.sync.title}</h1>
              <h3 style={styles.h3}>{t.sync.source}</h3>
              <pre style={styles.pre}>{docs.sync_engine.source}</pre>
              <table style={styles.table}>
                <tbody>
                  <tr><td style={styles.td}>{t.sync.intervalDefault}</td><td style={styles.td}><code style={styles.code}>{docs.sync_engine.interval_minutes_default} min</code></td></tr>
                  <tr><td style={styles.td}>{t.sync.retentionDefault}</td><td style={styles.td}><code style={styles.code}>{docs.sync_engine.retention_days_default} days</code></td></tr>
                  <tr><td style={styles.td}>{t.sync.bootDelay}</td><td style={styles.td}><code style={styles.code}>{docs.sync_engine.boot_delay_ms} ms</code></td></tr>
                </tbody>
              </table>

              <h2 style={styles.h2}>{t.sync.retentionExplainer}</h2>
              <div style={styles.callout("warn")}>
                <div style={styles.calloutTitle("warn")}>{t.sync.retentionDoes}</div>
                <div style={styles.calloutBody}>{docs.sync_engine.retention_explainer.what_it_does}</div>
              </div>
              <div style={styles.callout("ok")}>
                <div style={styles.calloutTitle("ok")}>{t.sync.retentionDoesNot}</div>
                <div style={styles.calloutBody}>{docs.sync_engine.retention_explainer.what_it_does_NOT}</div>
              </div>
              <p style={styles.p}>
                <strong>{t.sync.retentionWhere}</strong> {docs.sync_engine.retention_explainer.where_it_runs}
              </p>
              <p style={styles.p}>
                <strong>{t.sync.retentionSql}</strong>
              </p>
              <pre style={styles.pre}>{docs.sync_engine.retention_explainer.sql}</pre>

              <h2 style={styles.h2}>{t.sync.flow}</h2>
              <ol style={{ ...styles.p, paddingLeft: 18 }}>
                {docs.sync_engine.flow.map((s, i) => <li key={i}>{s}</li>)}
              </ol>
              <h2 style={styles.h2}>{t.sync.triggers}</h2>
              <ul style={{ ...styles.p, paddingLeft: 18 }}>
                {t.sync.triggersList(
                  docs.sync_engine.interval_minutes_default,
                  <code style={styles.code}>PUT /sync/config</code>,
                  <code style={styles.code}>POST /sync/run</code>
                ).map((line, i) => <li key={i}>{line}</li>)}
              </ul>
            </>
          )}

          {docs && section === "database" && (
            <>
              <h1 style={styles.h1}>{t.database.title}</h1>
              <table style={styles.table}>
                <tbody>
                  <tr><td style={styles.td}>{t.database.database}</td><td style={styles.td}><code style={styles.code}>{docs.database.database}</code></td></tr>
                  <tr><td style={styles.td}>{t.database.host}</td><td style={styles.td}><code style={styles.code}>{docs.database.host}</code></td></tr>
                  <tr><td style={styles.td}>{t.database.socket}</td><td style={styles.td}><code style={styles.code}>{docs.database.socket}</code></td></tr>
                </tbody>
              </table>
              <h2 style={styles.h2}>{t.common.tables}</h2>
              <table style={styles.table}>
                <thead>
                  <tr><th style={styles.th}>{t.common.table}</th><th style={styles.th}>{t.common.description}</th></tr>
                </thead>
                <tbody>
                  {docs.database.tables.map((tb) => (
                    <tr key={tb.name}>
                      <td style={styles.td}><code style={styles.code}>{tb.name}</code></td>
                      <td style={styles.td}>{tb.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {docs && section === "operations" && (
            <>
              <h1 style={styles.h1}>{t.operations.title}</h1>
              <h3 style={styles.h3}>{t.operations.restartApi}</h3>
              <pre style={styles.pre}>{docs.operations.restart_api}</pre>
              <h3 style={styles.h3}>{t.operations.manualSync}</h3>
              <p style={styles.p}>{docs.operations.manual_sync}</p>
              <h3 style={styles.h3}>{t.operations.checkStorage}</h3>
              <pre style={styles.pre}>{docs.operations.check_storage}</pre>
            </>
          )}

          {docs && section === "troubleshooting" && (
            <>
              <h1 style={styles.h1}>{t.troubleshooting.title}</h1>

              <div style={styles.callout("danger")}>
                <div style={styles.calloutTitle("danger")}>{t.troubleshooting.retentionHeader}</div>
                <div style={styles.calloutBody}>{t.troubleshooting.retentionDefault}</div>
              </div>

              {Object.entries(docs.troubleshooting).map(([k, v]) => {
                if (k === "retention_scope" || k === "retention_default") return null;
                return (
                  <div key={k}>
                    <h3 style={styles.h3}>{k.replace(/_/g, " ")}</h3>
                    <p style={styles.p}>{v}</p>
                  </div>
                );
              })}

              <div style={{ ...styles.intro, marginTop: 14 }}>
                <div style={styles.introLabel}>{t.troubleshooting.retentionKey}</div>
                <div style={styles.p}>
                  <code style={styles.code}>{docs.troubleshooting.retention_default}</code>
                </div>
                <p style={{ ...styles.p, marginTop: 8 }}>
                  {docs.troubleshooting.retention_scope}
                </p>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}